import { Dropdown, MenuProps } from 'antd'
import { TFunction } from 'i18next'
import { IDocument, IFolder } from 'interfaces'
import classes from './ContextMenu.module.scss'
import { useAppDispatch, useAppSelector } from 'store/hook'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAlignCenter, faChartPie, faCodeBranch, faFeather, faFolderOpen, faPuzzlePiece } from '@fortawesome/free-solid-svg-icons';
import Assets from 'assets/icons'
import { audioExtensions, convertableTypesFiles, editableExtensions, extToAnalysis, typesToSigning, typesWithoutVersions, videoExtensions } from 'helpers/filesExtensions'
import { requestDeleteEntity } from 'store/reducers/documentsCreator'
import { setIsOpenAnalysisSider } from 'store/slices/aiFunctionsDataSlice'
import cn from 'classnames'
import { widthOfSiderAnalytics } from 'components/DetailsComponent/AnalysisSider/AnalysisSider'
import { defaultSidebarWidth } from 'store/slices/windowStateSlice'

const isAvailableEditing = window.REACT_APP_IS_EDITING_ACTIVE !== 'REPLACE_REACT_APP_IS_EDITING_ACTIVE' ? window.REACT_APP_IS_EDITING_ACTIVE : process.env.REACT_APP_IS_EDITING_ACTIVE || 'false';

type ContextMenuProps = {
  entity: IDocument | IFolder;
  t: TFunction;
  renderFunc: () => any;
  moveModalOpenHandle?: () => void;
  renameModalHandle?: () => void;
  shareModalHandle?: () => void;
  detailsModalHandle?: (tab?: string) => void;
  isDeletedEntity?: boolean;
  type?: string;
  onSectionIconClick: (section: string, info?: any) => void
  onDownloadFile?: (entity: IDocument | IFolder, version: IDocument, isPdf?: boolean) => void;
  onDownloadArchive?: (folder: IFolder) => void;
  onBulkDownload?: (isPdf: boolean) => void;
  onBulkDownloadAsArchive?: () => void;
  onBulkMove?: () => void;
  onBulkDelete?: (entity: IDocument | IFolder) => void;
  openFolder?: (record: any) => void;
  openInfoModal?: () => void;
};

const ContextMenu = ({
  entity, t,
  renderFunc,
  moveModalOpenHandle,
  renameModalHandle,
  shareModalHandle,
  detailsModalHandle,
  isDeletedEntity,
  type,
  onSectionIconClick,
  onDownloadFile,
  onDownloadArchive,
  onBulkDownload,
  onBulkDownloadAsArchive,
  onBulkMove,
  onBulkDelete,
  openFolder,
  openInfoModal,
}: ContextMenuProps) => {
  
  const { currentFolder, folders, selectedDocument, selectedRowKeys, choosenDocuments, documents } = useAppSelector(store => store.documents);
  const { userFoldersPermissions, userRootPermissions } = useAppSelector((store) => store.permissions);
  const { userPermissions, userData } = useAppSelector(store => store.userData);
  const { selectedChat } = useAppSelector(store => store.chat);

  const dispatch = useAppDispatch();

  const normalizedName = entity?.extension?.toLocaleLowerCase() || '';
  const isEditableFormat = editableExtensions.includes(normalizedName);
  const isAvailableEditMode = isAvailableEditing === 'true' && entity?.permissions?.includes('can_edit_document');

  const sideBarWidth = Number(localStorage.getItem('sideBarWidth')) || defaultSidebarWidth;
  const isRootFolder = !currentFolder[0];
  const targetFolderPermissions = (isRootFolder && userRootPermissions)
    ? userRootPermissions.permissions
    : userFoldersPermissions?.find(folder => folder.id === currentFolder[0]?.id)?.permissions || entity.permissions;
    
  const permissionsOfCurrententity = entity?.permissions || selectedDocument?.permissions;  

  const isBulkMode = !type && selectedRowKeys.length > 1 && choosenDocuments.length > 1;
  const bulkHasFolders = choosenDocuments.some(({ key }) => (key as string).startsWith('folder'));
  const bulkHasFiles = choosenDocuments.some(({ key }) => !(key as string).startsWith('folder'));
  const bulkOnlyFiles = bulkHasFiles && !bulkHasFolders;
  const notExtraBtns = type !== 'documentDetails';

  const isExperimentalAvailable = userData?.is_experimental_available;
  const isTranscribedFormat = audioExtensions.includes(entity.extension) || videoExtensions.includes(entity.extension);
  
  const isEntityForbiddenToDelete = isBulkMode
    ? !choosenDocuments.every(({ permissions }) => permissions?.includes('can_delete'))
    : !permissionsOfCurrententity?.includes('can_delete');
    
  const isEntityForbiddenToShare = !permissionsOfCurrententity?.includes('can_sharing') || entity.is_confidential;
  
  const isAvailableFoldersToMove = Boolean(currentFolder[0] || folders[0]);
  const isAvailableMoveDocuments = isBulkMode
    ? targetFolderPermissions?.includes('can_delete') && isAvailableFoldersToMove
    : targetFolderPermissions?.includes('can_delete');
    
  const isAvailableEditingFolders = userPermissions.can_edit_folder_permissions;
  const isAvailableViewingVersions = !typesWithoutVersions.includes(entity.extension);
  const isAvailableAnalysis = extToAnalysis.includes(entity.extension);
  const isNeedToShowSigning = userPermissions.can_manage_signing && typesToSigning.includes(entity?.extension?.toLocaleLowerCase());
  const isAvailableDownloadPdf = isBulkMode
    ? choosenDocuments.every((doc) => {
        if (!doc.permissions?.includes('can_download_pdf')) return false;
        const extension = doc?.extension?.toLocaleLowerCase() || '';
        if (!convertableTypesFiles.includes(extension)) return false;

        const id = (doc?.key as string).split(' ')[2];
        const signedEl = documents?.find((d: IDocument) => d.id === id);
        if (signedEl && signedEl.signing_status !== null) return false;

        return true;
      })
    : convertableTypesFiles.includes(entity.extension) &&
      entity?.permissions?.includes('can_download_pdf') &&
      (entity as IDocument)?.signing_status === null;
      
  const isAvailableDownload = isBulkMode
    ? choosenDocuments.every(({ permissions }) => permissions?.includes('can_download'))
    : [...typesToSigning, 'xls', 'xlsx'].includes(entity.extension)
      ? entity?.permissions?.includes('can_download') || entity?.permissions?.includes('can_download_pdf')
      : entity?.permissions?.includes('can_download');
      
  const isAvailableDownloadArchive = isBulkMode
    ? choosenDocuments.every(({ permissions }) => permissions?.includes('can_download')) && choosenDocuments.length > 0
    : permissionsOfCurrententity?.includes('can_download');

  const handleAnalysisOpen = () => {
    const spaceForDoc = window.innerWidth - Number(localStorage.getItem('sideBarWidth'));
    onSectionIconClick && onSectionIconClick('', entity);
    dispatch(setIsOpenAnalysisSider({ open: true, width: spaceForDoc < 840 ? widthOfSiderAnalytics[2] : widthOfSiderAnalytics[1] }));
  };

   const menuItems: MenuProps['items'] = [];

   if(isBulkMode){

    if (!selectedChat?.id) {
      menuItems.push({
        label: (
          <span className={classes.spanItem}>
            <Assets.RelocateIcon /> {t('Documents.contextMenu.move')}
          </span>
        ),
        onClick: moveModalOpenHandle,
        className: classes.menuItem,
        disabled: !isAvailableMoveDocuments,
        key: 'bulkMove',
      });
    }    
 
    if (bulkOnlyFiles && isAvailableDownload) {
      menuItems.push({ type: 'divider', className: classes.divider });
 
      const downloadSubItems: MenuProps['items'] = [
        {
          label: (
            <span className={classes.spanItem}>
              <Assets.DownloadIcon /> {t('Documents.management.downloadAsDoc')}
            </span>
          ),
          onClick: () => onBulkDownload && onBulkDownload(false),
          className: classes.menuItem,
          key: 'bulkDownloadOriginal',
        },
      ];
 
      if (isAvailableDownloadPdf) {
        downloadSubItems.push({ type: 'divider', className: classes.divider });
        downloadSubItems.push({
          label: (
            <span className={classes.spanItem}>
              <Assets.FilePdfIcon /> {t('Documents.management.downloadAsPdf')}
            </span>
          ),
          onClick: () => onBulkDownload && onBulkDownload(true),
          className: classes.menuItem,
          key: 'bulkDownloadPdf',
        });
      }

      downloadSubItems.push({ type: 'divider', className: classes.divider });
      downloadSubItems.push({
        label: (
          <span className={classes.spanItem}>
            <Assets.FileZipIcon /> {t('Documents.contextMenu.downloadArchive')}
          </span>
        ),
        onClick: onBulkDownloadAsArchive,
        className: classes.menuItem,
        disabled: !isAvailableDownloadArchive,
        key: 'bulkDownloadArchive',
      });
 
      menuItems.push({
        label: (
          <span className={classes.spanItem}>
            <Assets.DownloadIcon /> {t('Documents.contextMenu.download')}
          </span>
        ),
        className: cn(classes.menuItem, classes.extraMenuItem),
        key: 'bulkDownload',
        children: downloadSubItems,
      });
    }
 
    !bulkOnlyFiles && menuItems.push({ type: 'divider', className: classes.divider });
    !bulkOnlyFiles && menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.FileZipIcon /> {t('Documents.contextMenu.downloadArchive')}
        </span>
      ),
      onClick: onBulkDownloadAsArchive,
      className: classes.menuItem,
      disabled: !isAvailableDownloadArchive,
      key: 'bulkDownloadArchive',
    });
 
    menuItems.push({ type: 'divider', className: classes.divider });
    menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.TrashIcon /> {t('Documents.contextMenu.delete')}
        </span>
      ),

      onClick: () => onBulkDelete?.(entity),
      className: classes.menuItem,
      disabled: isEntityForbiddenToDelete,
      key: 'bulkDelete',
    });

    menuItems.push({ type: 'divider', className: classes.divider });
    menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.InfoIcon /> {t('Documents.contextMenu.info')}
        </span>
      ),
      onClick: () => openInfoModal?.(),
      className: classes.menuItem,
      key: 'info',
    });
  }

  else if (entity.type === 'folder') {
    openFolder && menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <FontAwesomeIcon className={classes.faIcon} icon={faFolderOpen}/> {t('Documents.contextMenu.open')}
        </span>
      ),
      onClick: () => openFolder(entity),
      className: cn(classes.menuItem, classes.folderIcon),
      key: 'open',
    });
    openFolder && menuItems.push({ type: 'divider', className: classes.divider });

    menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.TextEditIcon /> {t('Documents.contextMenu.rename')}
        </span>
      ),
      onClick: renameModalHandle,
      className: classes.menuItem,
      disabled: !isAvailableEditingFolders,
      key: 'rename',
    });
    
    !selectedChat?.id && menuItems.push({ type: 'divider', className: classes.divider });
    !selectedChat?.id && menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.RelocateIcon /> {t('Documents.contextMenu.move')}
        </span>
      ),
      onClick: moveModalOpenHandle,
      className: classes.menuItem,
      disabled: !isAvailableMoveDocuments || !isAvailableFoldersToMove,
      key: 'move',
    });

    isAvailableDownloadArchive && menuItems.push({type: 'divider',className: classes.divider });
    
    isAvailableDownloadArchive && menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.FileZipIcon /> {t('Documents.contextMenu.downloadArchive')}
        </span>
      ),
      onClick: () => onDownloadArchive?.(entity as IFolder),
      className: classes.menuItem,
      key: 'downloadArchive',
    });

    menuItems.push({ type: 'divider', className: classes.divider });
    menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.TrashIcon /> {t('Documents.contextMenu.delete')}
        </span>
      ),
      onClick: () => dispatch(requestDeleteEntity({entity})),
      className: classes.menuItem,
      disabled: isEntityForbiddenToDelete,
      key: 'delete',
    });

    menuItems.push({ type: 'divider', className: classes.divider });
    menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.InfoIcon /> {t('Documents.contextMenu.info')}
        </span>
      ),
      onClick: () => openInfoModal?.(),
      className: classes.menuItem,
      key: 'info',
    });
  } else {
    notExtraBtns && menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.EyeIcon /> {t('Documents.contextMenu.open')}
        </span>
      ),
      onClick: () => detailsModalHandle && detailsModalHandle('view'),
      className: cn(classes.menuItem, classes.eyeIcon),
      key: 'view',
    });
    notExtraBtns && menuItems.push({ type: 'divider', className: classes.divider });

    isEditableFormat && isAvailableEditMode && Boolean(entity.signing_status === null) && notExtraBtns
      && menuItems.push({
        label: (
          <span className={classes.spanItem}>
            <Assets.FileEditIcon /> {t('Documents.contextMenu.edit')}
          </span>
        ),
        onClick: () => detailsModalHandle && detailsModalHandle('edit'),
        className: classes.menuItem,
        key: 'edit',
      });
    isEditableFormat && isAvailableEditMode && Boolean(entity.signing_status === null) && notExtraBtns
      && menuItems.push({ type: 'divider', className: classes.divider });

    isAvailableDownload && menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.TextEditIcon /> {t('Documents.contextMenu.rename')}
        </span>
      ),
      onClick: renameModalHandle,
      className: classes.menuItem,
      disabled: isEntityForbiddenToDelete,
      key: 'rename',
    });
    isAvailableDownload && menuItems.push({ type: 'divider', className: classes.divider,});
    
    !selectedChat?.id && menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.RelocateIcon /> {t('Documents.contextMenu.move')}
        </span>
      ),
      onClick: moveModalOpenHandle,
      className: classes.menuItem,
      disabled: !isAvailableMoveDocuments || !isAvailableFoldersToMove,
      key: 'move',
    });
    !selectedChat?.id && menuItems.push({ type: 'divider', className: classes.divider });

    menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.ShareIcon /> {t('Documents.contextMenu.sharing')}
        </span>
      ),
      onClick: shareModalHandle,
      className: classes.menuItem,
      disabled: isEntityForbiddenToShare,
      key: 'share',
    });

    const downloadSubItems: MenuProps['items'] = [];

    isAvailableDownload && downloadSubItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.DownloadIcon /> {t('Documents.contextMenu.download')}
        </span>
      ),
      onClick: () => onDownloadFile?.(entity, entity as IDocument),
      className: classes.menuItem,
      key: 'download',
    });

    downloadSubItems.length && isAvailableDownloadPdf && downloadSubItems.push({ type: 'divider', className: classes.divider });
    isAvailableDownloadPdf && downloadSubItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.FilePdfIcon /> {t('Documents.management.downloadAsPdf')}
        </span>
      ),
      onClick: () => onDownloadFile?.(entity, entity as IDocument, true),
      className: classes.menuItem,
      key: 'downloadPDF',
    });

    downloadSubItems.length && menuItems.push({ type: 'divider', className: classes.divider });
    if (downloadSubItems.length && downloadSubItems.length > 1) {
      downloadSubItems.length && menuItems.push({
        label: ( 
          <span className={classes.spanItem}>
            <Assets.DownloadIcon /> {t('Documents.contextMenu.download')}
          </span>
        ),
        className: cn(classes.menuItem, classes.extraMenuItem),
        key: 'downloading',
        children: downloadSubItems,
      });
    } else if (downloadSubItems.length === 1) {
      menuItems.push(downloadSubItems[0]);
    }

    const extraSubItems: MenuProps['items'] = [];
    
    isNeedToShowSigning && notExtraBtns && extraSubItems.push({
      label: (
        <span className={classes.spanItem}>
          <FontAwesomeIcon className={classes.faIcon} icon={faFeather}/> {t('Documents.contextMenu.signing')}
        </span>
      ),
      onClick:() => onSectionIconClick && onSectionIconClick('file_setting.signing', entity),
      className: classes.menuItem,
      key: 'signing',
    });

    extraSubItems.length && userPermissions.can_edit_agreement && notExtraBtns && extraSubItems.push({
      type: 'divider',
      className: classes.divider,
    });

    userPermissions.can_edit_agreement && notExtraBtns && extraSubItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.VerifyIcon /> {t('Documents.contextMenu.agreement')}
        </span>
      ),
      onClick:() => onSectionIconClick && onSectionIconClick('file_setting.agreement', entity),
      className: classes.menuItem,
      key: 'agreement',
    });

    extraSubItems.length && isTranscribedFormat && isExperimentalAvailable && notExtraBtns && !entity.is_confidential && extraSubItems.push({
      type: 'divider',
      className: classes.divider,
    });
    isTranscribedFormat && isExperimentalAvailable && notExtraBtns && !entity.is_confidential && extraSubItems.push({
      label: (
        <span className={classes.spanItem}>
          <FontAwesomeIcon
            className={classes.faIcon}
            rotation={270}
            icon={faAlignCenter}
          />
          {t('Documents.contextMenu.transcription')}
        </span>
      ),
      onClick: () => detailsModalHandle && detailsModalHandle('transcription'),
      className: classes.menuItem,
      key: 'transcription',
    });

    extraSubItems.length && isAvailableAnalysis && isExperimentalAvailable && notExtraBtns && extraSubItems.push({
      type: 'divider',
      className: classes.divider,
    });
    isAvailableAnalysis && isExperimentalAvailable && notExtraBtns && extraSubItems.push({
      label: (
      <span className={classes.spanItem}>
          <FontAwesomeIcon
            className={classes.faIcon}
            icon={faChartPie}
          />
          {t('Documents.contextMenu.analysis')}
        </span>
      ),
      onClick: handleAnalysisOpen,
      className: classes.menuItem,
      key: 'analysis',
    });

    extraSubItems.length && isAvailableViewingVersions && notExtraBtns && extraSubItems.push({
      type: 'divider',
      className: classes.divider,
    });
    
    isAvailableViewingVersions && notExtraBtns && extraSubItems.push({
      label: (
        <span className={classes.spanItem}>
          <FontAwesomeIcon className={classes.faIcon} icon={faCodeBranch} /> {t('Documents.contextMenu.versions')}
        </span>
      ),
      onClick: () => detailsModalHandle && detailsModalHandle('versions'),
      className: classes.menuItem,
      key: 'versions',
    });

    extraSubItems.length && menuItems.push({ type: 'divider', className: classes.divider });
    extraSubItems.length && menuItems.push({
      label: ( 
        <span className={classes.spanItem}>
          <FontAwesomeIcon className={classes.faIcon} icon={faPuzzlePiece}/>
          <span style={{ marginRight: 14 }}>{t('Documents.contextMenu.extra')}</span>
        </span>
      ),
      className: cn(classes.menuItem, classes.extraMenuItem),
      key: 'extra',
      children: extraSubItems,
    });

    menuItems.push({ type: 'divider', className: classes.divider });
    menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.TrashIcon /> {t('Documents.contextMenu.delete')}
        </span>
      ),
      onClick: () => dispatch(requestDeleteEntity({entity})),
      className: classes.menuItem,
      disabled: isEntityForbiddenToDelete,
      key: 'delete',
    });

    menuItems.push({ type: 'divider', className: classes.divider });
    menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.InfoIcon /> {t('Documents.contextMenu.info')}
        </span>
      ),
      onClick: () => openInfoModal?.(),
      className: classes.menuItem,
      key: 'info',
    });
  }
  
  return type ? (
    <Dropdown
      arrow={false}
      placement='bottomLeft'
      menu={{ items: menuItems }}
      trigger={['click']}
      disabled={isDeletedEntity}
      classNames={{ root: classes.addMenu }}
    >
      {renderFunc()}
    </Dropdown>
  ) : (
    <Dropdown
      styles={{ root: { left: (entity.pageX || 0) + sideBarWidth } }}
      arrow={false}
      placement='bottomLeft'
      open={entity.isOpenContextMenu}
      menu={{ items: menuItems }}
      trigger={['contextMenu']}
      classNames={{ root: classes.addMenu }}
    >
      {renderFunc()}
    </Dropdown>
  );
};

export default ContextMenu;
